import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Button, CircularProgress, Grid, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    ClinicMultiValueSelect,
    DailyAverageKpi,
    DateRangeFields,
    Header,
    LabDestinationVolumeChart,
    LabMultiValueSelect,
    ParticipationKpi,
    ScanVolumeByClinicChart,
    ScanVolumeByFirmwareVersionChart,
    ScanVolumeByScannerChart,
    ScanVolumeBySoftwareVersionChart,
    ScanVolumeByProcedureChart,
    Spinner,
    TotalScanVolumeChart,
    TotalScanVolumeKpi,
} from '../../components';
import {
    DashboardProvider,
    useDashboardContext,
} from './context';
import './styles.scss';

function DashboardContent() {
    const theme = useTheme();
    const matchUpLg = useMediaQuery(theme.breakpoints.up('lg'));
    const {
        analyticsData,
        clinics,
        exporting,
        exportFailed,
        failed,
        hasExportableAnalyticsData,
        labs,
        loading,
        maxDate,
        onExportAnalytics,
        minStartDate,
        onUpdateClinics,
        onUpdateDateRange,
        onUpdateLabs,
        selectedClinics,
        selectedEndDate,
        selectedLabs,
        selectedStartDate,
    } = useDashboardContext();
    const { scope } = localStorage;
    const scopeArray = (scope || '').split(' ');
    const canExportAnalytics = scopeArray.includes('analytics:export');
    const showClinicFilter = clinics.length > 0;
    const showLabFilter = labs.length > 0;
    const visibleFilterCount = Number(showClinicFilter) + Number(showLabFilter);
    const dateRangeLgWidth = canExportAnalytics
        ? (visibleFilterCount === 2 ? 6 : visibleFilterCount === 1 ? 8 : 10)
        : (visibleFilterCount === 2 ? 8 : visibleFilterCount === 1 ? 10 : 12);
    const totalScanVolumeChart = analyticsData ? (
        <TotalScanVolumeChart
            analytics={analyticsData.casesByCreatedDate}
            startDate={selectedStartDate}
            endDate={selectedEndDate}
        />
    ) : null;
    const scanVolumeByClinicChart = analyticsData ? (
        <ScanVolumeByClinicChart analytics={analyticsData.casesByClinic} />
    ) : null;
    const labDestinationVolumeChart = analyticsData ? (
        <LabDestinationVolumeChart
            analytics={analyticsData.casesByLab}
            totalAnalytics={analyticsData.casesByCreatedDate}
        />
    ) : null;
    const scanVolumeBySoftwareVersionChart = analyticsData ? (
        <ScanVolumeBySoftwareVersionChart analytics={analyticsData.casesBySoftwareVersion} />
    ) : null;
    const scanVolumeByFirmwareVersionChart = analyticsData ? (
        <ScanVolumeByFirmwareVersionChart analytics={analyticsData.casesByFirmwareVersion} />
    ) : null;
    const scanVolumeByScannerChart = analyticsData ? (
        <ScanVolumeByScannerChart analytics={analyticsData.casesByScanner} />
    ) : null;
    const procedureChart = <ScanVolumeByProcedureChart />;
    const exportButtonIcon = exporting
        ? <CircularProgress size={16} color="inherit" />
        : exportFailed
            ? <WarningAmberOutlinedIcon />
            : <FileDownloadOutlinedIcon />;

    return (
        <>
            <Header label="Dashboard" />
            <main className="dashboard-route-container">
                <Grid container spacing={2} marginBottom={2}>
                    {showClinicFilter && (
                        <Grid item lg={2} md={12} sm={12} xs={12}>
                            <ClinicMultiValueSelect
                                clinics={clinics}
                                selectedClinics={selectedClinics}
                                onSelectClinics={onUpdateClinics}
                            />
                        </Grid>
                    )}
                    {showLabFilter && (
                        <Grid item lg={2} md={12} sm={12} xs={12}>
                            <LabMultiValueSelect
                                labs={labs}
                                selectedLabs={selectedLabs}
                                onSelectLabs={onUpdateLabs}
                            />
                        </Grid>
                    )}
                    <Grid item lg={dateRangeLgWidth} md={12} sm={12} xs={12}>
                        <DateRangeFields
                            initialStartDate={selectedStartDate}
                            initialEndDate={selectedEndDate}
                            minStartDate={minStartDate}
                            maxDate={maxDate}
                            onChangeDateRange={onUpdateDateRange}
                        />
                    </Grid>
                    {canExportAnalytics && (
                        <Grid item lg={2} md={12} sm={12} xs={12} className="dashboard-filter-actions">
                            <Button
                                variant="contained"
                                className="dashboard-export-button"
                                disabled={loading || exporting || exportFailed || !hasExportableAnalyticsData}
                                fullWidth={!matchUpLg}
                                startIcon={exportButtonIcon}
                                onClick={onExportAnalytics}
                            >
                                Export
                            </Button>
                        </Grid>
                    )}
                </Grid>
                {loading && <Spinner type="page" />}
                {!loading && failed && <p className="text-red text-align-center">Failed to load dashboard data</p>}
                {!loading && !failed && analyticsData && (
                    <>
                        <Grid container spacing={2} marginBottom={2}>
                            <Grid item lg={4} md={6} xs={12}>
                                <TotalScanVolumeKpi analytics={analyticsData.casesByCreatedDate} />
                            </Grid>
                            <Grid item lg={4} md={6} xs={12}>
                                <DailyAverageKpi
                                    analytics={analyticsData.casesByCreatedDate}
                                    startDate={selectedStartDate}
                                    endDate={selectedEndDate}
                                />
                            </Grid>
                            <Grid item lg={4} md={6} xs={12}>
                                <ParticipationKpi analytics={analyticsData.participation} />
                            </Grid>
                        </Grid>
                        {/* If the screen is large, split into two columns that stack. */}
                        {/* If the screen is small, stack everything in a single column on top of each other. */}
                        {/* NOTE: This means that each chart needs to be added twice. */}
                        {matchUpLg ? (
                            <div className="dashboard-analytics-layout">
                                <div className="dashboard-analytics-layout__top">
                                    <div className="dashboard-analytics-layout__column">
                                        {totalScanVolumeChart}
                                        {labDestinationVolumeChart}
                                    </div>
                                    <div className="dashboard-analytics-layout__column">
                                        {scanVolumeByClinicChart}
                                        {procedureChart}
                                    </div>
                                </div>
                                {/* Put the version/scanner charts in their own bottom row on large screens. */}
                                <div className="dashboard-analytics-layout__bottom">
                                    <div className="dashboard-analytics-layout__panel">
                                        {scanVolumeBySoftwareVersionChart}
                                    </div>
                                    <div className="dashboard-analytics-layout__panel">
                                        {scanVolumeByFirmwareVersionChart}
                                    </div>
                                    <div className="dashboard-analytics-layout__panel">
                                        {scanVolumeByScannerChart}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    {totalScanVolumeChart}
                                </Grid>
                                <Grid item xs={12}>
                                    {scanVolumeByClinicChart}
                                </Grid>
                                <Grid item xs={12}>
                                    {labDestinationVolumeChart}
                                </Grid>
                                <Grid item xs={12}>
                                    {procedureChart}
                                </Grid>
                                <Grid item xs={12}>
                                    {scanVolumeBySoftwareVersionChart}
                                </Grid>
                                <Grid item xs={12}>
                                    {scanVolumeByFirmwareVersionChart}
                                </Grid>
                                <Grid item xs={12}>
                                    {scanVolumeByScannerChart}
                                </Grid>
                            </Grid>
                        )}
                    </>
                )}
            </main>
        </>
    );
}

function Dashboard() {
    return (
        <DashboardProvider>
            <DashboardContent />
        </DashboardProvider>
    );
}

export default Dashboard;
